import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiService } from '../services/api'

interface NotificationContextType {
  hasNotifications: boolean
  unreadCount: number
  checkNotifications: () => Promise<void>
  markAsRead: (id: string) => Promise<void>
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

interface NotificationProviderProps {
  children: ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [hasNotifications, setHasNotifications] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  const checkNotifications = async () => {
    try {
      const token = localStorage.getItem('token')
      
      if (token) {
        // Essayer d'abord d'utiliser l'endpoint optimisé pour le comptage
        try {
          const count = await apiService.getUnreadNotificationCount()
          const num = Number(count) || 0
          setUnreadCount(num)
          setHasNotifications(num > 0)
        } catch {
          // Fallback: récupérer les notifications (l'API peut retourner { data: [] })
          const raw = await apiService.getMyNotifications()
          const notifications = Array.isArray(raw) ? raw : (raw as any)?.data ?? []
          const unreadNotifications = notifications.filter(
            (n: any) => n.est_lue !== 1 && n.est_lue !== true
          )
          const num = unreadNotifications.length
          setUnreadCount(num)
          setHasNotifications(num > 0)
        }
      } else {
        setHasNotifications(false)
        setUnreadCount(0)
      }
    } catch (error) {
      // Erreur silencieuse pour les notifications
      setHasNotifications(false)
      setUnreadCount(0)
    }
  }

  const markAsRead = async (id: string) => {
    try {
      await apiService.markNotificationAsRead(id)
      // Recharger les notifications après marquage comme lu
      await checkNotifications()
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error)
    }
  }

  useEffect(() => {
    checkNotifications()
    
    // Vérifier toutes les 30 secondes
    const interval = setInterval(checkNotifications, 30000)
    return () => clearInterval(interval)
  }, [])

  return (
    <NotificationContext.Provider value={{ hasNotifications, unreadCount, checkNotifications, markAsRead }}>
      {children}
    </NotificationContext.Provider>
  )
}
